import { useEffect, useState } from "react";
import { Play, RefreshCw, Trash2, Edit3, Plus, CheckCircle2, AlertTriangle, Search, Database, Activity, HelpCircle, X, Upload, ExternalLink, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import ConnectorHealthPanel from "@/components/ConnectorHealthPanel";


const CATEGORY_LABELS = {
  lis_pendens: "Lis Pendens / NOD",
  eviction: "Eviction Filings",
  vacant_registry: "Vacant Registries",
  code_violation: "Code Violations",
  probate: "Probate (Manual)",
  divorce: "Divorce (Manual)",
};


const SOURCE_TYPE_LABELS = {
  socrata: "Socrata API",
  arcgis_rest: "ArcGIS REST",
  arcgis_hub: "ArcGIS Hub",
  carto: "CARTO SQL",
  manual_upload: "Manual CSV",
  csv_url: "CSV URL",
};


// Per source-type setup guide ,  shown in the help dialog so admins know
// exactly what fields to fill in for each connector type to work end-to-end.
const SETUP_GUIDES = {
  socrata: {
    title: "Socrata API connector",
    when: "Use for any open-data portal powered by Tyler Tech/Socrata (data.cityofchicago.org, opendata.dc.gov, data.cms.gov, etc.).",
    steps: [
      "Endpoint: full SoQL URL ending in .json (e.g. https://data.cityofchicago.org/resource/3uz9-23v7.json).",
      "Auth: set type='app_token' + env_key='SOCRATA_APP_TOKEN' (server reads the token from process env).",
      "Date field: the SoQL date column to filter on (e.g. created_date). Auto-populates {since} in the where clause.",
      "Where (optional): SoQL filter, e.g. case_type='LIS PENDENS' AND filing_date > '{since}'.",
      "Field map: {address, signal_date, source_id} → column names in the response.",
      "Click Test to fetch 3 rows. Click Run to upsert into opportunities. Enable scheduling once Test returns ok=true.",
    ],
    docs: "https://dev.socrata.com/foundry/data.cityofchicago.org/3uz9-23v7",
  },
  arcgis_rest: {
    title: "ArcGIS REST connector",
    when: "Direct query against an ArcGIS FeatureServer/MapServer layer's /query endpoint.",
    steps: [
      "Endpoint: must end in /FeatureServer/<n>/query or /MapServer/<n>/query (where <n> is the layer index).",
      "Date field: ArcGIS field name for the recorded/filing date (e.g. RECORDED_DATE, OPENED, FILED_DT).",
      "Where (optional): ArcGIS SQL filter. {since} substitutes the lookback cutoff (epoch ms).",
      "Field map: {address, signal_date, source_id} → ArcGIS attribute names from the layer schema.",
      "Tip: open the layer URL in your browser with ?f=json to see all available fields.",
      "Click Test → expect a payload with features[]. Then Run.",
    ],
    docs: "https://developers.arcgis.com/rest/services-reference/query-feature-service-layer-.htm",
  },
  arcgis_hub: {
    title: "ArcGIS Hub dataset",
    when: "Use when the city publishes data via an ArcGIS Hub site (data.baltimorecity.gov, opendata.dc.gov via ArcGIS, etc.) and you don't yet know the underlying REST URL.",
    steps: [
      "Open the public Hub dataset page (URL contains '/datasets/...').",
      "Click 'Add new connector' above → set source_type=arcgis_hub OR paste the Hub URL into the ArcGIS Hub Resolver below to auto-discover the FeatureServer URL.",
      "Once resolved, the connector is treated as an arcgis_rest config. Fill in date_field + field_map from the discovered schema.",
      "Click Test → then Run.",
    ],
    docs: "https://developers.arcgis.com/hub/",
  },
  carto: {
    title: "CARTO SQL connector",
    when: "Use for cities that expose their open data via a public CARTO SQL API (e.g. phl.carto.com).",
    steps: [
      "Endpoint: the SQL API base, e.g. https://phl.carto.com/api/v2/sql.",
      "Table: the CARTO table name to query (e.g. 'violations', 'eviction_filings').",
      "Date field: column to filter on, e.g. violationdate, filing_date.",
      "Where (optional): SQL fragment. {since} substitutes an ISO date string.",
      "Field map: {address, signal_date, source_id} → column names returned in 'rows'.",
      "Auth: usually 'none' (public CARTO endpoints).",
      "Click Test → expect rows[]. Then Run.",
    ],
    docs: "https://carto.com/developers/sql-api/",
  },
  manual_upload: {
    title: "Manual CSV connector",
    when: "Use when there's no programmatic endpoint (most probate/divorce indices, scanned PDFs, paid lists).",
    steps: [
      "Open the Data Sources panel (left sidebar) → choose a county source → click Upload CSV.",
      "Required columns: address, signal_date, owner_name, source_id (extra columns are preserved as metadata).",
      "Date format: ISO YYYY-MM-DD or MM/DD/YYYY. Address is geocoded server-side.",
      "This connector cannot Test/Run ,  uploads ingest immediately when posted.",
      "Schedule: set the cadence you'll manually re-upload (weekly recommended for divorce/probate dockets).",
    ],
  },
  csv_url: {
    title: "CSV URL connector",
    when: "Use when a county publishes a static CSV link that's refreshed periodically (some HUD vacancy registries).",
    steps: [
      "Endpoint: direct URL to the CSV file (must be publicly accessible, no auth).",
      "Field map: {address, signal_date, source_id} → column header names in the CSV.",
      "Click Run on a schedule ,  Test isn't implemented for CSV URLs yet (rows are streamed during Run).",
    ],
  },
};


/** Compute a short next-step message + status icon for the table cell.
 * Each step optionally carries an `action` key ,  when set, the row's
 * instruction text becomes a clickable chip that fires the matching handler.
 */
function getNextStep(c) {
  if (!c.enabled) {
    return { tone: "off",     label: "Toggle ON to activate", icon: "○", action: "toggle" };
  }
  if (c.source_type === "manual_upload") {
    // If the previous attempt (probe or manual run) failed, surface that
    // alongside the Upload action so operators see the failure context.
    if (c.last_status === "failed") {
      const msg = (c.last_message || "").slice(0, 60);
      return {
        tone: "manual",
        label: "Upload CSV",
        icon: "↑",
        action: "upload",
        warn: msg ? `Last attempt failed: ${msg}` : "Last attempt failed",
      };
    }
    return { tone: "manual",  label: "Upload CSV", icon: "↑", action: "upload" };
  }
  if (c.last_status === "failed") {
    const msg = (c.last_message || "").slice(0, 80) || "Click Test to diagnose";
    return { tone: "failed",  label: `Fix: ${msg}`, icon: "⚠", action: "edit" };
  }
  if (!c.endpoint) {
    return { tone: "warn",    label: "Set endpoint URL", icon: "○", action: "edit", focus: "endpoint" };
  }
  if ((c.auth?.type === "app_token") && !c.auth?.env_key) {
    return { tone: "warn",    label: "Set auth.env_key", icon: "○", action: "edit", focus: "auth_env_key" };
  }
  if (!c.date_field) {
    return { tone: "warn",    label: "Set date_field", icon: "○", action: "edit", focus: "date_field" };
  }
  if (!c.field_map?.address) {
    return { tone: "warn",    label: "Set field_map.address", icon: "○", action: "edit", focus: "field_map" };
  }
  if (c.last_status === "ok" && c.verified) {
    return { tone: "ok",      label: "Verified · runs on schedule", icon: "✓", action: null };
  }
  if (c.last_status === "ok") {
    return { tone: "near",    label: "Test passed ,  mark Verified", icon: "✓", action: "verify" };
  }
  if (c.last_status === "never" || !c.last_status) {
    return { tone: "todo",    label: "Click Test to verify", icon: "→", action: "test" };
  }
  return { tone: "near",      label: "Click Run to ingest", icon: "→", action: "run" };
}

const TONE_CLASS = {
  ok:     "text-emerald-300",
  near:   "text-cyan-300",
  todo:   "text-amber-300",
  warn:   "text-amber-400",
  failed: "text-rose-300",
  manual: "text-violet-300",
  off:    "text-slate-500",
};


export default function DiscoveryConfigsAdmin() {
  const [configs, setConfigs] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [filter, setFilter] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [busy, setBusy] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [helpFor, setHelpFor] = useState(null); // source_type key whose guide to display
  const [uploadFor, setUploadFor] = useState(null); // config cid whose CSV upload modal is open
  const [editFocusField, setEditFocusField] = useState(null); // focus a specific field when Edit opens
  // BLOCK 139 Part C ,  hide disabled/unfixable connectors by default (research
  // notes stay in the DB, just hidden from the default view). Same pattern as
  // the "Show test data" toggle on Opportunities.
  const [showAll, setShowAll] = useState(false);

  // BLOCK 39 Item 5 ,  HUD LIHTC ingest panel (moved here from Pending Review).
  const [hudStatus, setHudStatus] = useState(null);
  const [hudState, setHudState] = useState("CA");
  const [hudBusy, setHudBusy] = useState(false);
  const [hudLastResult, setHudLastResult] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/discovery/configs");
    setConfigs(r.data.configs || []);
    setByStatus(r.data.by_status || {});
    // Also refresh HUD status.
    try {
      const h = await api.get("/listings/ingest/hud-lihtc/status");
      setHudStatus(h.data);
    } catch { setHudStatus(null); }
  };

  const runHudIngest = async () => {
    setHudBusy(true);
    setHudLastResult(null);
    try {
      const r = await api.post(`/listings/ingest/hud-lihtc?state=${hudState}&limit=100`);
      setHudLastResult(r.data);
      if (r.data?.status === "Failed") {
        toast.error(r.data?.error?.slice(0, 120) || "HUD ingest failed");
      } else {
        toast.success(`HUD LIHTC ${hudState}: ${r.data.new} new, ${r.data.updated} updated, ${r.data.skipped} skipped`);
      }
    } catch (e) {
      const err = e?.response?.data?.detail || e?.message || "HUD ingest failed";
      setHudLastResult({ status: "Failed", error: err });
      toast.error(err);
    } finally {
      setHudBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy({ seed: true });
    try {
      const r = await api.post("/admin/discovery/configs/seed");
      toast.success(`Seed: inserted ${r.data.inserted}, skipped ${r.data.skipped}`);
      await load();
    } finally { setBusy({ seed: false }); }
  };

  const runAll = async () => {
    setBusy({ runAll: true });
    try {
      const r = await api.post("/admin/discovery/configs/run-all");
      const totals = Object.values(r.data || {}).reduce(
        (acc, x) => ({ found: acc.found + (x.found || 0), created: acc.created + (x.created || 0) }),
        { found: 0, created: 0 });
      toast.success(`Ran all: found ${totals.found}, created ${totals.created}`);
      await load();
    } finally { setBusy({ runAll: false }); }
  };

  const runOne = async (cid) => {
    setBusy({ ...busy, [cid]: true });
    try {
      const r = await api.post(`/admin/discovery/configs/${cid}/run`);
      if (r.data?.error) toast.error(r.data.error);
      else toast.success(`Ran: found ${r.data.found || 0}, created ${r.data.created || 0}`);
      await load();
    } finally { setBusy({ ...busy, [cid]: false }); }
  };

  const testOne = async (cid) => {
    setBusy({ ...busy, [`${cid}_test`]: true, __test_pending__: true });
    setTestResult(null);
    try {
      const r = await api.post(`/admin/discovery/configs/${cid}/test`);
      setTestResult({ cid, ...r.data });
      if (r.data?.ok) toast.success(`Test OK: ${r.data.found} records sampled`);
      else toast.error(`Test failed: ${r.data?.error || r.data?.status_code}`);
    } finally { setBusy((b) => ({ ...b, [`${cid}_test`]: false, __test_pending__: false })); }
  };

  const toggleEnabled = async (cfg) => {
    await api.patch(`/admin/discovery/configs/${cfg.cid}`, { enabled: !cfg.enabled });
    toast.success(cfg.enabled ? "Disabled" : "Enabled");
    await load();
  };

  const markVerified = async (cfg) => {
    try {
      await api.patch(`/admin/discovery/configs/${cfg.cid}`, { verified: true });
      toast.success(`${cfg.county} marked Verified`);
      await load();
    } catch {
      toast.error("Could not mark verified");
    }
  };

  // Inline instruction-chip dispatcher ,  wires the next-step text to the
  // matching action so admins never have to leave the page to fix the step.
  const runInstructionAction = (cfg, step) => {
    switch (step.action) {
      case "toggle":  return toggleEnabled(cfg);
      case "upload":  setUploadFor(cfg); return;
      case "edit":    setEditFocusField(step.focus || null); setEditing(cfg); return;
      case "verify":  return markVerified(cfg);
      case "test":    return testOne(cfg.cid);
      case "run":     return runOne(cfg.cid);
      default:        return null;
    }
  };

  const del = async (cfg) => {
    if (!window.confirm(`Delete connector ${cfg.county}, ${cfg.state} (${cfg.category})?`)) return;
    await api.delete(`/admin/discovery/configs/${cfg.cid}`);
    toast.success("Deleted");
    await load();
  };

  const probeManual = async () => {
    setBusy({ probe: true });
    try {
      const r = await api.post("/admin/discovery/configs/probe-manual");
      toast.success(`Probed manual sources: ${r.data.candidates_found} candidates`);
    } finally { setBusy({ probe: false }); }
  };

  const flagHighEviction = async () => {
    setBusy({ flag: true });
    try {
      const r = await api.post("/admin/discovery/configs/flag-high-eviction");
      toast.success(`Flagged ${r.data.flagged_opportunities} opportunities across ${r.data.high_eviction_counties} counties`);
    } finally { setBusy({ flag: false }); }
  };

  const runPhillyAbsenteeMf = async () => {
    setBusy({ ...busy, phillySweep: true });
    try {
      const r = await api.post("/admin/discovery/configs/philadelphia-absentee-mf/run");
      const d = r.data || {};
      toast.success(
        `Sweep complete: ${d.created || 0} new opportunities created, ${d.updated || 0} existing updated, ${d.skipped || 0} skipped (duplicates). Average CF Score: ${d.avg_score ?? ", "}.`
      );
      await load();
    } catch (e) {
      toast.error("Philadelphia sweep failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy((b) => ({ ...b, phillySweep: false })); }
  };

  const filtered = configs
    .filter((c) => (showAll ? !c.enabled : c.enabled))
    .filter((c) => catFilter === "all" || c.category === catFilter)
    .filter((c) => !filter || (c.county || "").toLowerCase().includes(filter.toLowerCase()) || c.state.includes(filter.toUpperCase()));
  const hiddenCount = configs.filter((c) => !c.enabled).length;

  return (
    <div className="space-y-6 pb-24" data-testid="discovery-configs-admin">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Admin / Discovery</div>
          <h1 className="font-heading text-4xl tracking-tight">County Connector Configs</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-3xl">
            Config-driven county data source system. Each row = one county portal. Socrata + ArcGIS REST
            runners read these configs to pull lis pendens, evictions, vacant registries, and code violations.
            Manual-upload rows (probate, divorce) periodically probed for new API availability.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={seed} disabled={busy.seed}
                  data-testid="config-seed-btn"
                  className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50">
            <Database className="h-3.5 w-3.5" /> {busy.seed ? "..." : "Re-seed defaults"}
          </button>
          <button onClick={probeManual} disabled={busy.probe}
                  data-testid="config-probe-btn"
                  className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50">
            <Search className="h-3.5 w-3.5" /> {busy.probe ? "..." : "Probe manual sources"}
          </button>
          <button onClick={flagHighEviction} disabled={busy.flag}
                  data-testid="config-flag-eviction-btn"
                  className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50">
            <Activity className="h-3.5 w-3.5" /> {busy.flag ? "..." : "Flag high-eviction"}
          </button>
          <button onClick={runPhillyAbsenteeMf} disabled={busy.phillySweep}
                  data-testid="philly-absentee-mf-btn"
                  className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
            <Activity className="h-3.5 w-3.5" /> {busy.phillySweep ? "Sweeping..." : "Philadelphia Absentee MF Sweep"}
          </button>
          <button onClick={runAll} disabled={busy.runAll}
                  data-testid="config-runall-btn"
                  className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50">
            <Play className="h-3.5 w-3.5" /> {busy.runAll ? "Running..." : "Run all enabled"}
          </button>
        </div>
      </header>

      {/* Live Connector Health widget */}
      <ConnectorHealthPanel compact={true} />

      {/* BLOCK 39 Item 5 ,  HUD LIHTC Multifamily auto-ingest, moved from
          Pending Review. Includes last-run result so an ingest that returns
          zero rows shows WHY (upstream 404 vs empty state vs missing key). */}
      {hudStatus && (
        <div className="ae-card p-5" data-testid="hud-ingest-panel">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <div className={`h-9 w-9 rounded-sm flex items-center justify-center shrink-0 ${hudStatus.has_api_key ? "bg-emerald-500/15" : "bg-amber-500/15"}`}>
                <Database className={`h-4 w-4 ${hudStatus.has_api_key ? "text-emerald-400" : "text-amber-400"}`} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-sky-400">Auto-Ingest · Reference</div>
                <div className="font-heading text-base mt-1">HUD LIHTC Multifamily</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {hudStatus.has_api_key
                    ? `${hudStatus.ingested_count} HUD-financed properties ingested to date.`
                    : "Set HUD_USER_API_KEY in backend/.env to activate live ingest."}
                </div>
                {!hudStatus.has_api_key && (
                  <a
                    href={hudStatus.register_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hud-register-link"
                    className="inline-flex items-center gap-1.5 text-[11px] text-amber-300 hover:text-amber-200 mt-2"
                  >
                    <KeyRound className="h-3 w-3" /> Get a free HUD User API key
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                value={hudState}
                onChange={(e) => setHudState(e.target.value.toUpperCase().slice(0, 2))}
                data-testid="hud-state-input"
                className="w-16 bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-1.5 text-xs uppercase font-mono"
                placeholder="CA"
                disabled={hudBusy}
              />
              <button
                onClick={runHudIngest}
                disabled={hudBusy}
                data-testid="hud-run-btn"
                className={`text-xs inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border ${
                  hudStatus.has_api_key
                    ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                } disabled:opacity-50`}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${hudBusy ? "animate-spin" : ""}`} />
                {hudBusy ? "Running..." : hudStatus.has_api_key ? "Run ingest" : "Test (will fail)"}
              </button>
            </div>
          </div>
          {hudLastResult && (
            <div
              data-testid="hud-last-result"
              className={`mt-3 px-3 py-2 rounded-sm border text-[11px] flex items-start gap-2 ${
                hudLastResult.status === "Failed"
                  ? "border-rose-500/40 bg-rose-500/5 text-rose-300"
                  : "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
              }`}
            >
              {hudLastResult.status === "Failed"
                ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                : <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
              <div className="font-mono text-[11px] break-all">
                {hudLastResult.status === "Failed"
                  ? <>Last run <b>failed</b>: {hudLastResult.error?.slice(0, 240) || "unknown error"}</>
                  : <>Last run <b>ok</b>: found {hudLastResult.found}, new {hudLastResult.new}, updated {hudLastResult.updated}, skipped {hudLastResult.skipped}</>}
              </div>
            </div>
          )}
          {!hudStatus.has_api_key && (
            <div className="mt-3 px-3 py-2 rounded-sm border border-amber-500/30 bg-amber-500/5 text-[11px] text-amber-300 flex items-start gap-2" data-testid="hud-key-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <div>
                Connector is wired and ready. Add <code className="text-amber-200">HUD_USER_API_KEY</code> to <code className="text-amber-200">backend/.env</code>, restart the backend, and ingest will go live across all 50 states. No code changes needed.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <SummaryCard label="Total" value={configs.length} testid="summary-total" />
        <SummaryCard label="Verified" value={byStatus.verified || 0} accent="emerald" testid="summary-verified" />
        <SummaryCard label="Enabled" value={byStatus.enabled || 0} accent="emerald" testid="summary-enabled" />
        <SummaryCard label="Disabled" value={byStatus.disabled || 0} accent="slate" testid="summary-disabled" />
        <SummaryCard label="Last OK" value={byStatus.last_ok || 0} accent="emerald" testid="summary-ok" />
        <SummaryCard label="Last Failed" value={byStatus.last_failed || 0} accent="rose" testid="summary-failed" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <input data-testid="config-search" value={filter} onChange={(e) => setFilter(e.target.value)}
                 placeholder="Search by county or state..."
                 className="w-full pl-8 pr-3 py-1.5 text-sm bg-[#0A0D12] border border-white/10 rounded-sm focus:border-emerald-500/60 outline-none" />
        </div>
        <select data-testid="config-cat-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
                className="text-xs bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-1.5">
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label
          className="flex items-center gap-2 text-xs text-slate-400 border border-white/10 rounded-sm px-3 py-1.5 cursor-pointer hover:bg-white/[0.03] transition-colors"
          title="Toggle between active connectors (default) and disabled ones (fabricated/wrong-dataset/retired , research notes preserved, never deleted)."
          data-testid="config-show-all-toggle-label"
        >
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            data-testid="config-show-all-toggle"
            className="h-3.5 w-3.5 accent-emerald-500"
          />
          <span>Show disabled only ({hiddenCount})</span>
        </label>
        <div className="ml-auto text-xs text-slate-500">{filtered.length} of {configs.length}</div>
      </div>

      {/* Test result panel */}
      {(busy[`__test_pending__`] || testResult) && (
        <div className="ae-card p-4 space-y-2" data-testid="config-test-result">
          {busy[`__test_pending__`] && !testResult && (
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <RefreshCw className="h-4 w-4 text-emerald-400 animate-spin" />
              Testing upstream endpoint... (this may take 1-10s)
            </div>
          )}
          {testResult && (
            <>
              <div className="flex items-center gap-2">
                {testResult.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
                <div className="text-sm text-slate-200">Test result: {testResult.cid}</div>
                <button onClick={() => setTestResult(null)} className="ml-auto text-xs text-slate-500 hover:text-slate-200">close</button>
              </div>
              <pre className="text-[10px] text-slate-400 bg-black/40 p-2 rounded-sm overflow-x-auto max-h-64">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}

      {/* Table */}
      <div className="ae-card p-0 overflow-hidden" data-testid="config-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/3 border-b border-white/8">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">County / City</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Source Type</th>
                <th className="text-left px-3 py-2">Schedule</th>
                <th className="text-left px-3 py-2">Last Run</th>
                <th className="text-left px-3 py-2">Instructions</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.cid} className="border-b border-white/5 hover:bg-white/3"
                    data-testid={`config-row-${c.cid}`}>
                  <td className="px-3 py-2 font-mono text-slate-300">{c.state}</td>
                  <td className="px-3 py-2">
                    <div className="text-slate-200 flex items-center gap-1.5">
                      {c.county}
                      {c.verified && (
                        <span title="Verified live endpoint" data-testid={`config-verified-${c.cid}`}
                              className="inline-flex items-center text-emerald-400">
                          <CheckCircle2 className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500">{c.motivation_flag}</div>
                    {c.notes && (
                      <div className="text-[10px] text-amber-400/70 mt-0.5 truncate max-w-[280px]" title={c.notes}>
                        ⚠ {c.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                      c.category === "lis_pendens" ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
                      : c.category === "eviction" ? "bg-orange-500/15 text-orange-300 border-orange-500/40"
                      : c.category === "vacant_registry" ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                      : c.category === "code_violation" ? "bg-sky-500/15 text-sky-300 border-sky-500/40"
                      : "bg-slate-500/15 text-slate-300 border-slate-500/40"
                    }`}>{CATEGORY_LABELS[c.category] || c.category}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{SOURCE_TYPE_LABELS[c.source_type] || c.source_type}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{c.schedule}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.last_status === "ok" && <span className="text-emerald-400">OK</span>}
                    {c.last_status === "failed" && <span className="text-rose-400" title={c.last_message}>FAILED</span>}
                    {c.last_status === "never" && <span className="text-slate-500">never</span>}
                    {c.last_run && <div className="text-[10px] text-slate-500">{fmtDateTime(c.last_run)}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs max-w-[260px]" data-testid={`config-instructions-${c.cid}`}>
                    {(() => {
                      const step = getNextStep(c);
                      const clickable = !!step.action;
                      const inner = (
                        <>
                          <span className={`font-mono shrink-0 ${TONE_CLASS[step.tone]}`}>{step.icon}</span>
                          <div className={`${TONE_CLASS[step.tone]} break-words text-left leading-snug`}>
                            {step.label}
                            {step.warn && (
                              <div className="text-[10px] text-rose-300/90 mt-0.5 italic" data-testid={`config-warn-${c.cid}`}>
                                ⚠ {step.warn}
                              </div>
                            )}
                          </div>
                        </>
                      );
                      return (
                        <div className="flex items-start gap-1.5 leading-snug">
                          {clickable ? (
                            <button
                              onClick={() => runInstructionAction(c, step)}
                              data-testid={`config-action-${step.action}-${c.cid}`}
                              title={`Take action: ${step.label}`}
                              className="flex items-start gap-1.5 flex-1 text-left hover:underline decoration-dotted underline-offset-2"
                            >
                              {inner}
                            </button>
                          ) : (
                            <div className="flex items-start gap-1.5 flex-1">{inner}</div>
                          )}
                          <button
                            onClick={() => setHelpFor(c.source_type)}
                            title={`Setup guide for ${SOURCE_TYPE_LABELS[c.source_type] || c.source_type}`}
                            data-testid={`config-help-${c.cid}`}
                            className="ml-auto shrink-0 text-slate-500 hover:text-emerald-400"
                          >
                            <HelpCircle className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => testOne(c.cid)} disabled={busy[`${c.cid}_test`] || c.source_type === "manual_upload"}
                              data-testid={`config-test-${c.cid}`}
                              className="px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border bg-white/3 text-slate-300 border-white/10 hover:bg-white/8 disabled:opacity-40">
                        {busy[`${c.cid}_test`] ? "..." : "Test"}
                      </button>
                      <button onClick={() => runOne(c.cid)} disabled={busy[c.cid] || !c.enabled || c.source_type === "manual_upload"}
                              data-testid={`config-run-${c.cid}`}
                              className="px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-40">
                        {busy[c.cid] ? <RefreshCw className="h-3 w-3 animate-spin inline" /> : "Run"}
                      </button>
                      <button onClick={() => toggleEnabled(c)}
                              data-testid={`config-toggle-${c.cid}`}
                              className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${
                                c.enabled
                                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                                  : "bg-slate-700/30 text-slate-400 border-slate-700/60"
                              }`}>
                        {c.enabled ? "ON" : "OFF"}
                      </button>
                      <button onClick={() => setEditing(c)} data-testid={`config-edit-${c.cid}`}
                              className="px-2 py-1 text-[10px] rounded-sm border bg-white/3 text-slate-300 border-white/10 hover:bg-white/8">
                        <Edit3 className="h-3 w-3" />
                      </button>
                      <button onClick={() => del(c)} data-testid={`config-delete-${c.cid}`}
                              className="px-2 py-1 text-[10px] rounded-sm border bg-rose-500/10 text-rose-400 border-rose-500/30 hover:bg-rose-500/20">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-12 text-center text-sm text-slate-500">
                  No configs match. <button onClick={seed} className="text-emerald-400 hover:underline ml-1">Re-seed defaults?</button>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add new */}
      <div className="flex justify-end">
        <button onClick={() => setShowAdd(true)}
                data-testid="config-add-btn"
                className="btn-primary text-sm inline-flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" /> Add new connector
        </button>
      </div>

      {/* Setup guide dialog ,  opens when admin clicks the (?) on any row */}
      {helpFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
             onClick={() => setHelpFor(null)}
             data-testid="config-help-overlay">
          <div className="ae-card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 bg-[#0A0D12]"
               onClick={(e) => e.stopPropagation()}
               data-testid="config-help-dialog">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">
                  Setup guide
                </div>
                <h2 className="font-heading text-xl text-slate-100 mt-1">
                  {SETUP_GUIDES[helpFor]?.title || SOURCE_TYPE_LABELS[helpFor] || helpFor}
                </h2>
              </div>
              <button onClick={() => setHelpFor(null)} data-testid="config-help-close"
                      className="text-slate-500 hover:text-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>

            {SETUP_GUIDES[helpFor] ? (
              <>
                <div className="text-sm text-slate-400 mb-4 leading-relaxed">
                  {SETUP_GUIDES[helpFor].when}
                </div>
                <ol className="space-y-2.5 text-sm text-slate-300">
                  {SETUP_GUIDES[helpFor].steps.map((s, i) => (
                    <li key={i} className="flex gap-3" data-testid={`config-help-step-${i}`}>
                      <span className="font-mono text-[11px] text-emerald-400 shrink-0 mt-0.5 w-5">{i + 1}.</span>
                      <span className="leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ol>
                {SETUP_GUIDES[helpFor].docs && (
                  <a href={SETUP_GUIDES[helpFor].docs} target="_blank" rel="noopener noreferrer"
                     className="mt-5 inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200"
                     data-testid="config-help-docs">
                    <ExternalLink className="h-3 w-3" /> External documentation
                  </a>
                )}
                {helpFor === "manual_upload" && (
                  <div className="mt-5 flex items-center gap-2 text-[11px] text-violet-300 border border-violet-500/30 bg-violet-500/10 rounded-sm p-2.5">
                    <Upload className="h-3.5 w-3.5 shrink-0" />
                    <span>Manual connectors do not have Test/Run buttons ,  uploads ingest automatically when posted via the Data Sources panel.</span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-slate-400">No guide available for source type &ldquo;{helpFor}&rdquo;.</div>
            )}
          </div>
        </div>
      )}

      {(showAdd || editing) && <ConfigEditor existing={editing} focusField={editFocusField} onClose={() => { setShowAdd(false); setEditing(null); setEditFocusField(null); }} onSaved={() => { load(); setShowAdd(false); setEditing(null); setEditFocusField(null); }} />}

      {uploadFor && (
        <UploadCsvDialog
          cfg={uploadFor}
          onClose={() => setUploadFor(null)}
          onSaved={() => { setUploadFor(null); load(); }}
        />
      )}
    </div>
  );
}


function UploadCsvDialog({ cfg, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fmap = cfg.field_map || {};
  const expected = [
    { col: fmap.address || "address", req: "required" },
    { col: fmap.signal_date || "signal_date", req: "optional" },
    { col: fmap.source_id || "source_id", req: "optional" },
    { col: fmap.city || "city", req: "optional" },
    { col: fmap.zip || "zip", req: "optional" },
    { col: "owner_name", req: "optional" },
  ];

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        `/admin/discovery/configs/${cfg.cid}/upload-csv`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(
        `Uploaded ${r.data.rows_in_file} rows · ${r.data.created} new · ${r.data.updated} updated · ${r.data.skipped} skipped`,
      );
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
         onClick={onClose}
         data-testid="upload-csv-overlay">
      <div className="ae-card max-w-lg w-full p-5 space-y-4 bg-[#0A0D12]"
           onClick={(e) => e.stopPropagation()}
           data-testid="upload-csv-dialog">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-400 flex items-center gap-1.5">
              <Upload className="h-3 w-3" /> CSV Upload
            </div>
            <div className="font-heading text-lg text-slate-100 mt-1">{cfg.county}, {cfg.state}</div>
            <div className="text-[11px] text-slate-500">
              {CATEGORY_LABELS[cfg.category] || cfg.category} · {cfg.motivation_flag}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"
                  data-testid="upload-csv-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="text-xs text-slate-300 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            Expected CSV columns (header row required)
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {expected.map((e) => (
              <div key={e.col} className="flex items-center gap-1.5 text-[11px]">
                <span className={`px-1.5 py-0.5 rounded-sm border ${
                  e.req === "required"
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                    : "bg-white/3 text-slate-400 border-white/10"
                }`}>{e.req}</span>
                <code className="text-slate-200 font-mono">{e.col}</code>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-500">
            Each row is tagged with {cfg.county}, {cfg.state} · life_event_type={cfg.life_event_type}.
            Date format ISO YYYY-MM-DD (or MM/DD/YYYY); address is geocoded server-side.
          </div>
        </div>

        <label
          htmlFor="upload-csv-input"
          className="block border border-dashed border-white/15 rounded-sm p-4 text-center text-xs text-slate-400 hover:border-violet-500/40 hover:text-slate-200 cursor-pointer"
          data-testid="upload-csv-drop"
        >
          <input
            id="upload-csv-input"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="upload-csv-input"
            className="hidden"
          />
          {file
            ? <span className="text-violet-200 font-mono">{file.name} · {(file.size/1024).toFixed(1)} KB</span>
            : <span>Click to choose a .csv file (UTF-8, ≤10 MB)</span>}
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="upload-csv-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !file}
            data-testid="upload-csv-submit"
            className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-violet-500/15 text-violet-200 border-violet-500/50 hover:bg-violet-500/25 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {busy ? "Uploading…" : "Upload & ingest"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SummaryCard({ label, value, accent, testid }) {
  const cls = accent === "emerald" ? "text-emerald-300"
    : accent === "rose" ? "text-rose-300"
    : accent === "slate" ? "text-slate-400"
    : "text-slate-100";
  return (
    <div className="ae-card p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-heading text-2xl tracking-tight mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}


function ConfigEditor({ existing, focusField, onClose, onSaved }) {
  const [form, setForm] = useState(existing || {
    state: "", county: "", source_type: "socrata", category: "lis_pendens",
    motivation_flag: "Lis Pendens", life_event_type: "Lis Pendens",
    endpoint: "", auth: { type: "none" }, date_field: "", where: "",
    field_map: { address: "address", signal_date: "" },
    lookback_days: 30, schedule: "weekly", enabled: true,
  });
  const [busy, setBusy] = useState(false);

  // Focus + scroll the requested field on mount so the admin lands on the
  // exact input they need to fix.
  useEffect(() => {
    if (!focusField) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-edit-field="${focusField}"]`);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        const input = el.querySelector("input, textarea, select");
        if (input) input.focus();
        el.classList.add("ring-2", "ring-emerald-500/60");
        setTimeout(() => el.classList.remove("ring-2", "ring-emerald-500/60"), 2200);
      }
    }, 80);
    return () => clearTimeout(t);
  }, [focusField]);

  const save = async () => {
    setBusy(true);
    try {
      if (existing) {
        await api.patch(`/admin/discovery/configs/${existing.cid}`, form);
        toast.success("Updated");
      } else {
        await api.post("/admin/discovery/configs", form);
        toast.success("Created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="config-editor-modal">
      <div className="bg-[#0A0D12] border border-white/10 rounded-sm w-full max-w-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <h2 className="font-heading text-xl">{existing ? "Edit" : "Add"} connector config</h2>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Inp label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v.toUpperCase() })} />
          <Inp label="County / City" value={form.county} onChange={(v) => setForm({ ...form, county: v })} />
          <Sel label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })}
               options={Object.entries(CATEGORY_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
          <Sel label="Source Type" value={form.source_type} onChange={(v) => setForm({ ...form, source_type: v })}
               options={Object.entries(SOURCE_TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
          <Inp label="Motivation Flag" value={form.motivation_flag} onChange={(v) => setForm({ ...form, motivation_flag: v })} />
          <Inp label="Life Event Type" value={form.life_event_type} onChange={(v) => setForm({ ...form, life_event_type: v })} />
          <div className="col-span-2" data-edit-field="endpoint"><Inp label="Endpoint URL" value={form.endpoint} onChange={(v) => setForm({ ...form, endpoint: v })} /></div>
          <div data-edit-field="date_field"><Inp label="Date Field" value={form.date_field || ""} onChange={(v) => setForm({ ...form, date_field: v })} /></div>
          <Inp label="Lookback (days)" type="number" value={form.lookback_days} onChange={(v) => setForm({ ...form, lookback_days: parseInt(v) || 30 })} />
          <div className="col-span-2"><Inp label="Where Clause (use {since})" value={form.where || ""} onChange={(v) => setForm({ ...form, where: v })} /></div>
          <Sel label="Schedule" value={form.schedule} onChange={(v) => setForm({ ...form, schedule: v })}
               options={[{value:"daily",label:"Daily"},{value:"weekly",label:"Weekly"},{value:"monthly",label:"Monthly"}]} />
          <div className="col-span-2" data-edit-field="auth_env_key">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Auth env key {form.auth?.type === "app_token" && <span className="text-amber-400 normal-case">required for Socrata</span>}</div>
            <input
              type="text"
              value={form.auth?.env_key || ""}
              onChange={(e) => setForm({ ...form, auth: { ...(form.auth || {}), type: "app_token", env_key: e.target.value } })}
              className="w-full inp-dark text-sm"
              placeholder="SOCRATA_APP_TOKEN"
            />
          </div>
          <div className="col-span-2 text-[10px] text-slate-500">Field Map (JSON):</div>
          <div className="col-span-2" data-edit-field="field_map">
            <textarea value={JSON.stringify(form.field_map || {}, null, 2)}
                      onChange={(e) => { try { setForm({ ...form, field_map: JSON.parse(e.target.value) }); } catch { /* ignore parse */ } }}
                      rows={5} className="w-full inp-dark font-mono text-[10px]" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="config-save-btn" className="btn-primary text-sm disabled:opacity-50">
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


function Inp({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full inp-dark text-sm" />
    </div>
  );
}


function Sel({ label, value, onChange, options }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full inp-dark text-sm">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
